import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/admin/ui";

const STATUS = {
  not_connected: ["Not connected", "bg-zinc-100 text-zinc-600 border-zinc-200"],
  test: ["Test mode", "bg-amber-50 text-amber-700 border-amber-200"],
  setup_required: ["Setup required", "bg-sky-50 text-sky-700 border-sky-200"],
  live: ["Live", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  disabled: ["Disabled", "bg-zinc-100 text-zinc-600 border-zinc-200"],
};

export default function Payments() {
  const [d, setD] = useState(null);
  const [refreshingTax, setRefreshingTax] = useState(false);

  useEffect(() => {
    api.get("/admin/payments").then((r) => {
      setD(r.data);
    });
  }, []);
  if (!d) return <div className="py-20 text-center text-zinc-400"><Loader2 className="animate-spin mx-auto" /></div>;
  const s = d.stripe;
  const [stripeLabel, stripeCls] = STATUS.disabled;
  const paypalLive = d.paypal.status === "active";
  const refreshTax = async () => {
    setRefreshingTax(true);
    try {
      await api.post("/admin/payments/tax/refresh");
      toast.success("Official Florida rate cache cleared.");
    } catch (error) {
      toast.error(errMsg(error));
    }
    setRefreshingTax(false);
  };

  const [paypalLabel, paypalCls] = paypalLive ? STATUS.live : STATUS.setup_required;

  return (
    <div data-testid="admin-payments-page" className="max-w-3xl">
      <Link to="/admin/settings" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 mb-4"><ArrowLeft size={15} /> Settings</Link>
      <h1 className="text-2xl font-bold mb-6">Payments</h1>
      <div className="space-y-4">
        <Card title="Stripe — disabled" action={<span data-testid="stripe-status-badge" className={`text-xs font-medium px-2.5 py-1 rounded-full border ${stripeCls}`}>{stripeLabel}</span>}>
          <p className="text-sm text-zinc-600">Customer card checkout is disabled. Existing Stripe credentials, webhooks, refunds, and historical records remain available to administrators.</p>
          <p className="mt-4 text-xs text-zinc-500">Stored account mode: {s.mode === "live" ? "Live" : "Test"}.</p>
        </Card>

        <Card
          title="Florida tax collection"
          description="Official Florida Department of Revenue address lookup. No paid tax provider is enabled."
          action={(
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-full border ${d.tax.active
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-100 text-zinc-600 border-zinc-200"}`}
              data-testid="florida-tax-status"
            >
              {d.tax.active ? "Active" : "Inactive"}
            </span>
          )}
        >
          <div className="space-y-3 text-sm text-zinc-600" data-testid="florida-tax-rules">
            <p
              className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-amber-950"
              data-testid="admin-tax-disabled-warning"
            >
              Checkout sales-tax collection is currently disabled. Tax may still be legally required.
            </p>
            <p>Florida deliveries use 6% state tax plus the official delivery-address county surtax. Required shipping is included; surtax is capped at $5,000 per physical item.</p>
            <p>Orders outside Florida are not charged Florida tax and are flagged for nexus review.</p>
            <p>Business jurisdiction: Kissimmee, Osceola County, Florida. Rates are checked from the official source and cached for 24 hours.</p>
            <div className="flex flex-wrap gap-3 text-xs">
              {d.tax.source && <a href={d.tax.source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-zinc-800 underline"><ExternalLink size={12} /> Address lookup</a>}
              {d.tax.rate_table_source && <a href={d.tax.rate_table_source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-zinc-800 underline"><ExternalLink size={12} /> Current county rates</a>}
              <button type="button" onClick={refreshTax} disabled={refreshingTax} className="inline-flex items-center gap-1 text-zinc-800 underline disabled:opacity-50" data-testid="florida-tax-refresh-button">
                {refreshingTax ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh official lookup
              </button>
            </div>
          </div>
        </Card>

        <Card title="PayPal — active provider" action={<span data-testid="paypal-status-badge" className={`text-xs font-medium px-2.5 py-1 rounded-full border ${paypalCls}`}>{paypalLabel}</span>}>
          <p className="text-sm text-zinc-600">
            {paypalLive
              ? "Customer checkout uses verified PayPal LIVE credentials. Payment is marked paid only after a successful capture or verified webhook event."
              : "PayPal checkout stays unavailable until LIVE credentials, webhook ID, and the connection check all pass."}
          </p>
          <p className="mt-4 text-xs text-zinc-500" data-testid="paypal-webhook-status">
            Connection: {d.paypal.connection_verified ? "verified" : "not verified"} · Webhook: {d.paypal.webhook_signature_verified_at
              ? `signature verified ${new Date(d.paypal.webhook_signature_verified_at).toLocaleString()}`
              : d.paypal.webhook_configured ? "configured; awaiting a signed PayPal delivery" : "needs attention"} · Checkout: {d.paypal.checkout_enabled ? "enabled" : "disabled"}.
          </p>
        </Card>
      </div>
    </div>
  );
}
